import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { GetTime, GetHrv, UpdateKiosk } from '../lib/DeviceData';

import ttsAudio from '../assets/sound/tts_08.mp3';
import ttsAudio_en from '../assets/sound/en/8.mp3';
import ttsAudio_jp from '../assets/sound/jp/8.mp3';
import ttsAudio_cn from '../assets/sound/cn/8.mp3';
import ttsAudio_mn from '../assets/sound/mn/9.mp3';

const Readys2 = () => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const audioRef = useRef(null);

    const userInfo = { ...(location.state || {}) };

    const { device_sn, device_no, member_no, name, sex, birth, start, end, returnPath } = userInfo;

    const [result_data, setDatas] = useState([]);
    const [hrv_data, setHrv] = useState([]);

    const [bucketTime, setbucketTime] = useState('');
    const [HrTime, sethrTime] = useState(0);
    const [RrTime, setrrTime] = useState(0);

    const [closestHRVData, setClosestHRVData] = useState(null);
    const [dbok, setdbok] = useState(0);

    const [Device_sn, SetDevice_sn] = useState('');
    const [Device_no, SetDevice_no] = useState(0);
    const [Member_no, SetMemeber_no] = useState(0);
    const [Name, SetName] = useState('');
    const [Sex, SetSex] = useState(0);
    const [Birth, SetBirth] = useState('');

    const [Kiosk, setKiosk] = useState({
        device_sn: '',
        device_no: 0,
        member_no: 0,
        name: '',
        sex: 0,
        birth: '',
        feel: 0,
        heart_avg: 0,
        resp_avg: 0,
        rmssd: 0,
        sdnn: 0,
        pnn50: 0,
        baevsky_si: 0,
        result_data: null,
        date: '',
    });

    const toNum = v => {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };

    const avg = arr => {
        const v = arr.filter(x => x != null);
        if (v.length === 0) return null;
        return v.reduce((a, b) => a + b, 0) / v.length;
    };

    useEffect(() => {
        if (!device_no || !start || !end) {
            alert('결과 계산에 필요한 측정 정보가 없습니다.');
            navigate('/monitoring/realtimelist');
            return;
        }

        GetTime(device_no, start, end, res => {
            setDatas(res);
        });

        GetHrv(device_no, res => {
            setHrv(res);
        });
    }, [device_no, start, end, navigate]);

    useEffect(() => {
        if (!result_data?.rows || result_data.rows.length === 0) return;

        const array_bucket = [];
        const array_hr = [];
        const array_rr = [];

        result_data.rows.forEach(item => {
            array_bucket.push(moment(item.bucket).format('YYYY-MM-DD HH:mm:ss'));
            array_hr.push(Number(item.hr || 0));
            array_rr.push(Number(item.rr || 0));
        });

        const avgHr = array_hr.length > 0 ? Math.round(array_hr.reduce((a, b) => a + b, 0) / array_hr.length) : 0;

        const avgRr = array_rr.length > 0 ? Math.round(array_rr.reduce((a, b) => a + b, 0) / array_rr.length) : 0;

        setbucketTime(array_bucket[0] || moment().format('YYYY-MM-DD HH:mm:ss'));
        sethrTime(avgHr);
        setrrTime(avgRr);

        SetDevice_sn(device_sn || '');
        SetDevice_no(Number(device_no || 0));
        SetMemeber_no(Number(member_no || 0));
        SetName(name || '');
        SetSex(Number(sex || 0));
        SetBirth(birth || '');
    }, [result_data, device_sn, device_no, member_no, name, sex, birth]);

    useEffect(() => {
        if (!hrv_data?.rows || hrv_data.rows.length === 0) return;

        const allBcg = [];

        for (const row of hrv_data.rows) {
            const bcg = row?.data?.bcg_results;
            if (Array.isArray(bcg)) {
                allBcg.push(...bcg);
            }
        }

        if (allBcg.length === 0) return;

        const valid = allBcg.filter(x => {
            const rm = toNum(x?.hrv_analysis_data?.rmssd);
            return rm != null && rm > 0;
        });

        if (valid.length === 0) return;

        const avgHr = avg(valid.map(x => toNum(x?.heart_rate)));
        const fallbackTarget = toNum(hrv_data.rows[0]?.data?.heart_rate);
        const targetHeartRate = avgHr ?? fallbackTarget ?? toNum(valid[0]?.heart_rate) ?? 0;

        const closest = valid.reduce((prev, curr) => {
            const ph = toNum(prev?.heart_rate) ?? 0;
            const ch = toNum(curr?.heart_rate) ?? 0;
            return Math.abs(ch - targetHeartRate) < Math.abs(ph - targetHeartRate) ? curr : prev;
        });

        const rmssdAvg = avg(valid.map(x => toNum(x?.hrv_analysis_data?.rmssd)));
        const sdnnAvg = avg(valid.map(x => toNum(x?.hrv_analysis_data?.sdnn)));
        const pnn50Avg = avg(valid.map(x => toNum(x?.hrv_analysis_data?.pnn50)));
        const baiAvg = avg(valid.map(x => toNum(x?.hrv_analysis_data?.baevsky_si)));

        setClosestHRVData({
            rmssd: rmssdAvg != null ? Number(rmssdAvg.toFixed(2)) : 0,
            sdnn: sdnnAvg != null ? Number(sdnnAvg.toFixed(2)) : 0,
            pnn50: pnn50Avg != null ? Number(pnn50Avg.toFixed(2)) : 0,
            baevsky_si: baiAvg != null ? Number(baiAvg.toFixed(2)) : 0,
            _sample_count: valid.length,
            _target_hr: targetHeartRate,
            _closest_hr: toNum(closest?.heart_rate) ?? 0,
        });
    }, [hrv_data]);

    useEffect(() => {
        const audio = audioRef.current;
        if (audio) {
            audio.play().catch(err => {
                console.warn('Autoplay failed:', err);
            });
        }
    }, []);

    const clickResult = () => {
        const rmssd = Number(closestHRVData?.rmssd ?? 0);
        const pnn50 = Number(closestHRVData?.pnn50 ?? 0);
        const sdnn = Number(closestHRVData?.sdnn ?? 0) + 20;
        const baevsky_si = Number(closestHRVData?.baevsky_si ?? 0);

        const kioskPayload = {
            device_sn: device_sn || '',
            device_no: Number(device_no || 0),
            member_no: Number(member_no || 0),
            name: name || '',
            sex: Number(sex || 0), // 남자 1, 여자 2
            birth: birth || '',
            feel: 0,
            heart_avg: Number(HrTime || 0),
            resp_avg: Number(RrTime || 0),
            rmssd: Number(rmssd),
            sdnn: Number(sdnn),
            pnn50: Number(pnn50),
            baevsky_si: Number(baevsky_si),
            result_data: null,
            date: moment().format('YYYY-MM-DD HH:mm:ss'),
        };

        setKiosk(kioskPayload);

        UpdateKiosk(kioskPayload, res => {
            setdbok(res);

            navigate('/stress/result', {
                state: {
                    device_sn: device_sn || '',
                    device_no: Number(device_no || 0),
                    member_no: Number(member_no || 0),
                    name: name || '',
                    sex: Number(sex || 0), // 남자 1, 여자 2
                    birth: birth || '',
                    feel: 0,
                    date: bucketTime || moment().format('YYYY-MM-DD HH:mm:ss'),
                    hr: Number(HrTime || 0),
                    rr: Number(RrTime || 0),
                    sdnn: Number(sdnn),
                    rmssd: Number(rmssd),
                    pnn50: Number(pnn50),
                    baevsky_si: Number(baevsky_si),
                    result_data: null,
                    returnPath: returnPath || '/monitoring/realtimelist',
                },
            });
        });
    };

    const lng = i18n.language;

    return (
    <div className='stress_content'>
        <main className="main-container">
            {lng === 'ko' && <audio ref={audioRef} src={ttsAudio} autoPlay />}
            {lng === 'en' && <audio ref={audioRef} src={ttsAudio_en} autoPlay />}
            {lng === 'jp' && <audio ref={audioRef} src={ttsAudio_jp} autoPlay />}
            {lng === 'cn' && <audio ref={audioRef} src={ttsAudio_cn} autoPlay />}
            {lng === 'mn' && <audio ref={audioRef} src={ttsAudio_mn} autoPlay />}

            <div className="Top_loading">
                <h2>
                    {t('측정이 완료되었습니다.')}
                    <br />
                    {t('아래버튼을 눌러')}
                    <br />
                    {t('결과를 확인해보세요.')}
                </h2>

                <div className="start-btn-area">
                    <button className="Start" onClick={clickResult}>
                        {t('결과보기')}
                    </button>
                </div>
            </div>
        </main>
    </div>
    );
};

export default Readys2;
